import { useCallback, useEffect, useRef, useState } from 'react';
import type { ServiceDay } from '../../../../../services/simulation/runReport';

/** Service minutes per real second; the same steps as the old results timeline. */
export const PLAY_SPEEDS = [2, 6, 15, 40] as const;
export type PlaySpeed = (typeof PLAY_SPEEDS)[number];

const SERVICE_MS_PER_REAL_MS_PER_SPEED = 60;
const DEFAULT_SPEED: PlaySpeed = PLAY_SPEEDS[1];
/** A background tab throttles frames; one long gap must not jump the clock hours ahead. */
const MAX_FRAME_MS = 250;

export interface Playback {
  /** Null until the service day is known. */
  readonly timeMs: number | null;
  readonly playing: boolean;
  readonly speed: PlaySpeed;
  readonly togglePlay: () => void;
  readonly setSpeed: (speed: PlaySpeed) => void;
  /** Moves to a moment and pauses, as scrubbing and jumping to an event do. */
  readonly seek: (timeMs: number) => void;
}

/** Service-day clock for one run. Playing advances `speed` service minutes per real second, measured by elapsed time. */
export function usePlayback(runId: string | null, day: ServiceDay | null): Playback {
  // State is tagged with the run and day it belongs to, so switching runs restarts the clock paused.
  const key = runId && day ? `${runId}:${day.startMs}` : null;
  const [time, setTime] = useState<{ key: string; ms: number } | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [speed, setSpeed] = useState<PlaySpeed>(DEFAULT_SPEED);
  const playing = key != null && playingKey === key;
  const timeMs = day ? (time && time.key === key ? time.ms : day.startMs) : null;
  const latest = useRef(timeMs);
  latest.current = timeMs;

  useEffect(() => {
    if (!playing || !day || !key) return undefined;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const elapsed = Math.min(now - last, MAX_FRAME_MS);
      const next = Math.min(day.endMs, (latest.current ?? day.startMs) + speed * SERVICE_MS_PER_REAL_MS_PER_SPEED * elapsed);
      last = now;
      setTime({ key, ms: next });
      if (next >= day.endMs) {
        setPlayingKey(null);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, speed, day, key]);

  const togglePlay = useCallback(() => {
    if (!day || !key) return;
    if (!playing && (latest.current ?? day.startMs) >= day.endMs) setTime({ key, ms: day.startMs });
    setPlayingKey(playing ? null : key);
  }, [day, key, playing]);

  const seek = useCallback(
    (ms: number) => {
      if (!day || !key) return;
      setPlayingKey(null);
      setTime({ key, ms: Math.min(day.endMs, Math.max(day.startMs, ms)) });
    },
    [day, key]
  );

  return { timeMs, playing, speed, togglePlay, setSpeed, seek };
}
